import React, { useState, useEffect } from 'react';
import { TriangleAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import professionalStore from "../../store/professional-store";
import { observer } from "mobx-react-lite";

const EmailVerificationBanner: React.FC = observer(() => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { t } = useTranslation();
  
  // Afficher la bannière si l'email n'est pas vérifié
  useEffect(() => {
    // Réinitialiser l'état visible quand le statut de vérification change
    if (professionalStore.getProfileProfessional?.emailVerified) {
      setIsVisible(false);
    } else {
      // Petit délai pour que l'animation soit visible après le chargement de la page
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [professionalStore.getProfileProfessional?.emailVerified]);

  // Fermer la bannière
  const closeBanner = () => {
    setIsClosing(true);
    
    // Attendre la fin de l'animation avant de cacher complètement
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };
  // Si l'email est vérifié ou si la bannière n'est pas visible, ne rien afficher
  if (professionalStore.getProfileProfessional?.emailVerified || !isVisible) {
    return null;
  }

  return (
    <div 
      className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ease-in-out w-[90%] sm:w-auto ${
        isClosing ? '-translate-y-20 opacity-0' : 'translate-y-0 opacity-100'
      } ${isVisible ? 'opacity-100' : 'opacity-0 -translate-y-20'}`}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-amber-200 dark:border-amber-800 max-w-lg w-full overflow-hidden mx-2 sm:mx-0">
        <div className="px-3 sm:px-5 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center">
          <div className="flex items-center space-x-3 sm:space-x-4 flex-1">
            <div className="flex-shrink-0">
              <span className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                <TriangleAlert className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 animate-bounce" />
              </span>
            </div>
            <div className="flex-1">
              <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                {t('emailVerificationBanner.title', 'Vérifiez votre boîte mail')}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 pr-8 sm:pr-0">
                {t('emailVerificationBanner.description', 'Cliquez sur le lien dans l\'email pour activer votre compte')}
              </p>
            </div>
          </div>
          <div className="absolute top-3 right-3 sm:relative sm:top-auto sm:right-auto sm:flex sm:items-center sm:space-x-4 sm:ml-8">
            <button
              type="button"
              onClick={closeBanner}
              className="p-1 rounded-full text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default EmailVerificationBanner;
